import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useNotAccommodatedPilgrimsReport, useNotAccommodatedPilgrimsReportMutation } from "@/services/reports";
import { cn } from "@/utils/helpers";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";

export const NotAccommodatedPilgrimsReport = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);

  // =====================
  // SIDE EFFECTS
  // =====================
  const {
    mutate: generateNotAccommodatedPilgrimsReport,
    isPending: isGeneratingNotAccommodatedPilgrimsReport,
  } = useNotAccommodatedPilgrimsReportMutation();

  const {
    data: notAccommodatedPilgrimsReportRes,
    isLoading: isLoadingNotAccommodatedPilgrimsReport,
    refetch,
    isRefetching: isRefetchingNotAccommodatedPilgrimsReport,
  } = useNotAccommodatedPilgrimsReport({
    params: { format: "json", page },
    enabled: isGeneratingNotAccommodatedPilgrimsReport,
  });

  const notAccommodatedPilgrimsReports = notAccommodatedPilgrimsReportRes?.data?.data || [];
  const meta = notAccommodatedPilgrimsReportRes?.data?.meta || {};

  const form = useForm({
    defaultValues: {
      format: "pdf",
    },
    disabled: isGeneratingNotAccommodatedPilgrimsReport,
  });

  const formText = {
    json: {
      buttonText: t("VIEW_REPORTS"),
    },
    pdf: {
      buttonText: t("EXPORT_PDF"),
    },
    excel: {
      buttonText: t("EXPORT_EXCEL"),
    },
  };

  const format = form.watch("format");
  const formContent = formText[format];
  const isJson = format === "json";

  useEffect(() => {
    if (isJson) {
      refetch();
    }
  }, [page, isJson, refetch]);

  const {
    control,
    formState: { errors },
  } = form;

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("GROUP_NAME") },
      controllerProps: {
        name: "group_name",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            id="group_name"
            placeholder={t("GROUP_NAME")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("GROUP_NUMBER") },
      controllerProps: {
        name: "group_number",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            id="group_number"
            placeholder={t("GROUP_NUMBER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("ORGANIZER") },
      controllerProps: {
        name: "mission_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="mission_id"
            placeholder={t("ORGANIZER")}
            status={error ? "error" : undefined}
            optionKey="missions"
            options={(m) => ({
              value: m.id,
              label: (
                <LabelValue.Value
                  value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")}
                />
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("NATIONALITY") },
      controllerProps: {
        name: "nationality_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="nationality_id"
            placeholder={t("NATIONALITY")}
            status={error ? "error" : undefined}
            optionKey="nationalities"
            options={(n) => ({
              value: n.id,
              label: <SelectCountry.Display value={n.id} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("COUNTRY") },
      controllerProps: {
        name: "country_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="country_id"
            placeholder={t("COUNTRY")}
            status={error ? "error" : undefined}
            optionKey="nationalities"
            options={(c) => ({
              value: c.id,
              label: <SelectCountry.Display value={c.id} />,
            })}
          />
        ),
      },
    },
  ];

  const columns = [
    {
      name: t("BORDER_NUMBER"),
      key: "border_number",
    },
    {
      name: t("GROUP_NAME"),
      key: "group_name",
    },
    {
      name: t("PILGRIM_NAME"),
      key: "pilgrim_name",
    },
    {
      name: t("PASSPORT_NUMBER"),
      key: "passport_number",
    },
    {
      name: t("VISA_NUMBER"),
      key: "visa_number",
    },
    {
      name: t("SOURCE"),
      key: "source",
    },
    {
      name: t("NATIONALITY"),
      key: "nationality",
    },
  ];

  const onSubmit = (data) => {
    if (isJson) {
      return refetch();
    }
    generateNotAccommodatedPilgrimsReport({ params: data });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/madina/reports/pilgrim-reports`;
  const isLoading =
    isGeneratingNotAccommodatedPilgrimsReport ||
    isLoadingNotAccommodatedPilgrimsReport || isRefetchingNotAccommodatedPilgrimsReport;

  return (
    <div>
      <PageHeader title={t("NOT_ACCOMMODATED_PILGRIMS_REPORT")} sticky={false} />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            {...{
              controllers: ctrls.map((config) => ({
                ...config,
                labelProps: {
                  ...config.labelProps,
                  className: cn("fw-bold", config.labelProps.className),
                },
                controllerProps: {
                  ...config.controllerProps,
                  control,
                  errors,
                },
                layout: {
                  colProps: { xs: 6, md: 6 },
                },
              })),
            }}
          />
          <ActionsFooter>
            <Button disabled={isLoading} className="btn btn-primary" type="submit">
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button 
            onClick={() => form.reset()}
            disabled={isLoading} className="btn btn-outline-primary btn-sm" type="button">
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={notAccommodatedPilgrimsReports}
            columns={columns}
            loading={
              isLoadingNotAccommodatedPilgrimsReport ||
              isRefetchingNotAccommodatedPilgrimsReport
            }
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 10,
              onChange: (nextPage) => {
                setPage(nextPage);
              },
            }}
          />
        )}
      </Container>
    </div>
  );
};

